/**
 * Delivery Date Refresher + Property Backfill
 *
 * Two cart-line jobs that run every page load:
 *
 *   1. Refresh stale delivery windows when a customer returns after 24+ hours.
 *   2. Backfill `Cancellation Policy`, `Return Policy`, and the delivery-window
 *      pair onto lines that entered the cart through a path that bypasses the
 *      PDP add-to-cart form — third-party search apps (Searchanise's
 *      Add-to-Cart button), cart permalinks, browser extensions, etc.
 *
 * Note: Shopify's /cart/change.js REPLACES the properties object — it does NOT
 * merge. Every PATCH below sends the full merged set so we never strip existing
 * properties.
 */
(function() {
  'use strict';

  var STALE_THRESHOLD_DAYS = 1;
  var CANCELLATION_POLICY_VALUE = '1-Hour Cancellation Window';
  var RETURN_POLICY_VALUE = '30 Day Return Policy';
  var CUSTOM_BUILD_RETURN_POLICY_VALUE = 'Final Sale - No Returns';

  function isCustomBuild(existing) {
    var builderSource = String(existing['_builder_source'] || existing['Builder Source'] || '');
    return /shade|bunk|spinweld/i.test(builderSource) || hasProp(existing, '_Spinweld Job ID');
  }

  async function processCart() {
    try {
      var response = await fetch('/cart.js');
      var cart = await response.json();
      if (!cart.items || cart.items.length === 0) return;

      // Recover older unmarked lines from current server-rendered cart markup.
      // One read per pass, detached parsing only: never execute fetched scripts.
      var deliveryDocument = null;
      if (cart.items.some(function(item) { return !getDomDeliveryForLine(item.key); })) {
        deliveryDocument = await loadDeliveryDocument();
      }
      var nowSeconds = Math.floor(Date.now() / 1000);
      var anyUpdated = false;

      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var existing = item.properties || {};
        var merged = mergeProperties(existing);
        var changed = false;

        // Backfill static policies if missing.
        if (!hasProp(existing, 'Cancellation Policy')) {
          merged['Cancellation Policy'] = CANCELLATION_POLICY_VALUE;
          changed = true;
        }
        if (isCustomBuild(existing) && existing['Return Policy'] !== CUSTOM_BUILD_RETURN_POLICY_VALUE) {
          merged['Return Policy'] = CUSTOM_BUILD_RETURN_POLICY_VALUE;
          changed = true;
        } else if (!hasProp(existing, 'Return Policy')) {
          merged['Return Policy'] = RETURN_POLICY_VALUE;
          changed = true;
        }

        // Refresh / backfill delivery window. Sources, in order:
        //   - DOM span rendered by _delivery-window-compute (cart drawer or cart
        //     page) — a fresh server-side recompute that applies current supplier
        //     lead times, live inventory, and holiday-shutdown padding
        //   - day-shifting the existing window (fallback only; it cannot know
        //     about shutdowns or lead-time changes made since add-to-cart)
        var existingWindow = existing['_Estimated Delivery Window'];
        var existingCalcAt = parseInt(existing['_delivery_calculated_at'], 10);

        var currentDelivery = getDomDeliveryForLine(item.key);
        if (!currentDelivery && deliveryDocument) currentDelivery = getDomDeliveryForLine(item.key, deliveryDocument);
        var lippertOwned = existing['_delivery_source'] === 'lippert' ||
          (currentDelivery && currentDelivery.source === 'lippert');
        if (lippertOwned) {
          // Supplier dates are evidence, not a reusable duration. Never day-shift them.
          // No current DOM evidence means unknown, even for a previously dated cart line.
          var nextWindow = currentDelivery ? currentDelivery.window : 'Delivery estimate unavailable';
          var nextSource = currentDelivery ? currentDelivery.source : 'lippert';
          var nextUnknown = !currentDelivery || currentDelivery.unknown;
          if (existingWindow !== nextWindow || existing['_delivery_source'] !== nextSource ||
              existing['_delivery_unknown'] !== String(nextUnknown)) {
            merged['_Estimated Delivery Window'] = nextWindow;
            merged['_delivery_source'] = nextSource;
            merged['_delivery_unknown'] = String(nextUnknown);
            merged['_delivery_calculated_at'] = String(nowSeconds);
            changed = true;
          }
        } else if (!currentDelivery) {
          // No current identity/evidence: preserve the saved value, but do not
          // invent a newer date by shifting an unidentified legacy line.
        } else if (existingWindow && existingCalcAt) {
          var staleDays = Math.floor((nowSeconds - existingCalcAt) / 86400);
          if (staleDays >= STALE_THRESHOLD_DAYS) {
            var fresh = currentDelivery;
            var refreshed = fresh ? fresh.window : shiftDates(existingWindow, staleDays);
            if (refreshed && refreshed !== existingWindow) {
              merged['_Estimated Delivery Window'] = refreshed;
              merged['_delivery_calculated_at'] = String(nowSeconds);
              changed = true;
            }
          }
        } else {
          // Missing — backfill from DOM span if we can find one.
          var dom = currentDelivery;
          if (dom) {
            merged['_Estimated Delivery Window'] = dom.window;
            merged['_delivery_calculated_at'] = String(nowSeconds);
            changed = true;
          }
        }

        if (changed) {
          await updateCartItem(item.key, merged, item.quantity);
          anyUpdated = true;
        }
      }

      if (anyUpdated) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { scrollToTop: false }
        }));
      }
    } catch (error) {
      console.error('Cart property refresh failed:', error);
    }
  }

  function hasProp(props, key) {
    var v = props[key];
    return v !== undefined && v !== null && v !== '';
  }

  function mergeProperties(existing) {
    var out = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) {
        out[k] = existing[k];
      }
    }
    return out;
  }

  async function loadDeliveryDocument() {
    var controller = new AbortController();
    var timeout = setTimeout(function() { controller.abort(); }, 5000);
    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      var response = await fetch(root + 'cart', {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) return null;
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  function getDomDeliveryForLine(lineKey, rootDocument) {
    if (!lineKey) return null;
    var selector = '[data-cart-delivery][data-line-key="' + cssEscape(lineKey) + '"]';
    var span = (rootDocument || document).querySelector(selector);
    if (!span) return null;
    var source = span.getAttribute('data-delivery-source') || '';
    var unknown = span.getAttribute('data-delivery-unknown') === 'true';
    if (unknown) return { window: 'Delivery estimate unavailable', source: source, unknown: true };
    var label = span.getAttribute('data-window-label');
    if (label) return { window: label, source: source, unknown: false };
    var minTs = parseInt(span.getAttribute('data-min-ts'), 10);
    var maxTs = parseInt(span.getAttribute('data-max-ts'), 10);
    if (!minTs || !maxTs) return null;
    var minDate = new Date(minTs * 1000);
    var maxDate = new Date(maxTs * 1000);
    return { window: formatDateRange(minDate, maxDate), source: source, unknown: false };
  }

  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/(["\\])/g, '\\$1');
  }

  function shiftDates(windowStr, days) {
    var parts = windowStr.split(' – ');
    if (parts.length !== 2) return null;

    var currentYear = new Date().getFullYear();
    var minDate = parseDate(parts[0], currentYear);
    var maxDate = parseDate(parts[1], currentYear);
    if (!minDate || !maxDate) return null;

    minDate.setDate(minDate.getDate() + days);
    maxDate.setDate(maxDate.getDate() + days);
    return formatDateRange(minDate, maxDate);
  }

  function parseDate(dateStr, baseYear) {
    var date = new Date(dateStr + ', ' + baseYear);
    if (isNaN(date.getTime())) return null;

    // Year rollover: dates more than 6 months in the past are next year's.
    var now = new Date();
    var sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    if (date < sixMonthsAgo) {
      date.setFullYear(baseYear + 1);
    }
    return date;
  }

  function formatDateRange(minDate, maxDate) {
    var options = { month: 'long', day: 'numeric' };
    var minStr = minDate.toLocaleDateString('en-US', options);
    var maxStr = maxDate.toLocaleDateString('en-US', options);
    return minStr + ' – ' + maxStr;
  }

  async function updateCartItem(key, mergedProperties, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, properties: mergedProperties })
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processCart);
  } else {
    processCart();
  }

  // Re-run after the cart drawer/page re-renders — the data-cart-delivery
  // spans we need for the delivery window only exist in the DOM at that point.
  // Idempotent: if nothing needs changing, the next pass POSTs nothing.
  document.documentElement.addEventListener('cart:rerendered', processCart);
})();
